// The formula grammar the model ledger is written in, and the evaluator for it.
//
// It lives in a library because three places must agree on what a formula means to the last
// decimal: the ledger as it is stored, the gates that admit a simulator over it, and the page
// that recomputes those same figures while a reader drags a control. A second implementation of
// npv() is a second answer to "what is this worth", and the reader would see both.
//
// The fourth place is the model that writes the ledger, `ledger_helper.py` on the runner. It is Python,
// so it cannot share this code; it shares its rules instead, and the library's fixture tests run
// both over the same figures and refuse a difference.

export type TToken =
  | { type: "num"; value: number }
  | { type: "key"; value: string }
  | { type: "fn"; value: string }
  | { type: "op"; value: string };

export type TNode =
  | { t: "num"; v: number }
  | { t: "key"; k: string }
  | { t: "neg"; a: TNode }
  | { t: "bin"; op: "+" | "-" | "*" | "/" | "**"; a: TNode; b: TNode }
  | { t: "series"; stem: string }
  | { t: "call"; fn: TFunctionName; args: TNode[] };

export type TRelationNode = { parts: TNode[]; ops: TComparison[] };

// The closed set of calls a formula may make, the same one bread's manifests and toast's twin
// evaluate: everything here terminates, holds no state and exists in Excel too.
const FUNCTIONS = ["series", "npv", "irr", "payback", "sum", "min", "max", "last"] as const;
export type TFunctionName = (typeof FUNCTIONS)[number];
const COMPARISONS = ["<=", ">=", "==", "!=", "<", ">"] as const;
export type TComparison = (typeof COMPARISONS)[number];

// The model writes Python literals: `.5` and `12_000` are numbers there, and a formula that ran
// clean in the sandbox but that this reader refused left every such figure marked as an error.
const NUMBER_RE = /^(?:\d[\d_]*(?:\.[\d_]*)?|\.\d[\d_]*)(?:[eE][+-]?\d+)?/;
const KEY_TOKEN_RE = /^_?[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)*/;
// A period is any four digits, as the models that write the ledgers spell one; a sibling with
// other digits after the stem is a mistake to name, not a row to skip.
export const YEAR_SUFFIX_RE = /\.(\d{4})$/;
const DIGIT_SUFFIX_RE = /\.\d+$/;

// A return the case does not have at these settings: flows that never cross zero have no rate,
// a running total that never comes back has no payback, a discount rate at or below -1 discounts
// nothing. Told apart from a malformed formula so a page can show a dash that says so, instead of
// the published number, which would claim a rate the case does not have.
export class NoReturnError extends Error {}

const tokenize = (formula: string): TToken[] => {
  const tokens: TToken[] = [];
  let at = 0;
  while (at < formula.length) {
    const rest = formula.slice(at);
    const space = /^\s+/.exec(rest);
    if (space) {
      at += space[0].length;
      continue;
    }
    const number = NUMBER_RE.exec(rest);
    if (number) {
      const value = Number(number[0].replace(/_/g, ""));
      // `1e400` is a literal Python parses to infinity, and nothing downstream should ever see one.
      if (!Number.isFinite(value)) throw new Error(`${number[0]} is not a finite number`);
      tokens.push({ type: "num", value });
      at += number[0].length;
      continue;
    }
    const key = KEY_TOKEN_RE.exec(rest);
    if (key) {
      const isCall = /^\s*\(/.test(rest.slice(key[0].length));
      tokens.push(isCall ? { type: "fn", value: key[0] } : { type: "key", value: key[0] });
      at += key[0].length;
      continue;
    }
    const comparison = COMPARISONS.find((op) => rest.startsWith(op));
    if (comparison) {
      tokens.push({ type: "op", value: comparison });
      at += comparison.length;
      continue;
    }
    if (rest.startsWith("**")) {
      tokens.push({ type: "op", value: "**" });
      at += 2;
      continue;
    }
    if ("+-*/(),".includes(rest[0])) {
      tokens.push({ type: "op", value: rest[0] });
      at += 1;
      continue;
    }
    throw new Error(`unexpected "${rest[0]}" at position ${at + 1}`);
  }
  return tokens;
};

const isFunctionName = (name: string): name is TFunctionName => (FUNCTIONS as readonly string[]).includes(name);
const isComparison = (value: string): value is TComparison => (COMPARISONS as readonly string[]).includes(value);

// Python precedence, which is what a formula written by the generation follows:
// unary minus binds looser than `**`, so `-2 ** 2` is -4, and `**` is right-associative.
const createParser = (tokens: TToken[]) => {
  let pos = 0;
  const peek = () => tokens[pos];
  const takeOp = (value: string) => {
    const token = tokens[pos];
    if (token?.type === "op" && token.value === value) {
      pos += 1;
      return true;
    }
    return false;
  };

  const parseCall = (fn: string): TNode => {
    if (!isFunctionName(fn)) throw new Error(`unknown function ${fn}`);
    if (!takeOp("(")) throw new Error(`missing "(" after ${fn}`);
    if (fn === "series") {
      const token = tokens[pos];
      if (token?.type !== "key") throw new Error("series() takes the stem of a family of yearly figures");
      pos += 1;
      if (!takeOp(")")) throw new Error("missing closing parenthesis");
      return { t: "series", stem: token.value };
    }
    const args: TNode[] = [];
    if (!takeOp(")")) {
      do {
        args.push(parseSum());
      } while (takeOp(","));
      if (!takeOp(")")) throw new Error("missing closing parenthesis");
    }
    // `min` and `max` are Python's, which take either one series or several numbers, and a model
    // reaches for `max(0, ...)` to floor a figure as readily as for `max(series(...))`.
    if (fn === "min" || fn === "max") {
      if (args.length < 1) throw new Error(`${fn}() takes a series or two or more numbers`);
    } else {
      const arity = fn === "npv" ? 2 : 1;
      if (args.length !== arity) throw new Error(`${fn}() takes ${arity} argument${arity === 1 ? "" : "s"}`);
    }
    return { t: "call", fn, args };
  };
  const parseAtom = (): TNode => {
    const token = tokens[pos];
    if (!token) throw new Error("unexpected end of formula");
    pos += 1;
    if (token.type === "num") return { t: "num", v: token.value };
    if (token.type === "key") return { t: "key", k: token.value };
    if (token.type === "fn") return parseCall(token.value);
    if (token.value === "(") {
      const inner = parseSum();
      if (!takeOp(")")) throw new Error("missing closing parenthesis");
      return inner;
    }
    throw new Error(`unexpected "${token.value}"`);
  };
  const parsePower = (): TNode => {
    const base = parseAtom();
    if (takeOp("**")) return { t: "bin", op: "**", a: base, b: parseUnary() };
    return base;
  };
  const parseUnary = (): TNode => {
    if (takeOp("-")) return { t: "neg", a: parseUnary() };
    if (takeOp("+")) return parseUnary();
    return parsePower();
  };
  const parseProduct = (): TNode => {
    let node = parseUnary();
    for (;;) {
      if (takeOp("*")) node = { t: "bin", op: "*", a: node, b: parseUnary() };
      else if (takeOp("/")) node = { t: "bin", op: "/", a: node, b: parseUnary() };
      else return node;
    }
  };
  const parseSum = (): TNode => {
    let node = parseProduct();
    for (;;) {
      if (takeOp("+")) node = { t: "bin", op: "+", a: node, b: parseProduct() };
      else if (takeOp("-")) node = { t: "bin", op: "-", a: node, b: parseProduct() };
      else return node;
    }
  };

  return {
    parseSum,
    peek,
    takeComparison: () => {
      const token = tokens[pos];
      if (token?.type === "op" && isComparison(token.value)) {
        pos += 1;
        return token.value;
      }
      return null;
    }
  };
};

// Exported so the RQASim gates evaluate the twin's own vars and its base vector against the same
// grammar the ledger was stored under, rather than carrying a second evaluator that can drift.
export const parseFormula = (formula: string): TNode => {
  const tokens = tokenize(formula);
  if (tokens.length === 0) throw new Error("empty formula");
  const parser = createParser(tokens);
  const root = parser.parseSum();
  const rest = parser.peek();
  if (rest) throw new Error(`unexpected "${rest.value}"`);
  return root;
};

// A relation is a chain of comparisons over arithmetic, read as Python chains them:
// `a <= b <= c` means both hold.
export const parseRelation = (relation: string): TRelationNode => {
  const tokens = tokenize(relation);
  if (tokens.length === 0) throw new Error("empty relation");
  const parser = createParser(tokens);
  const parts = [parser.parseSum()];
  const ops: TComparison[] = [];
  for (;;) {
    const op = parser.takeComparison();
    if (!op) break;
    ops.push(op);
    parts.push(parser.parseSum());
  }
  const rest = parser.peek();
  if (rest) throw new Error(`unexpected "${rest.value}"`);
  if (ops.length === 0) throw new Error("a relation needs a comparison");
  return { parts, ops };
};

export type TSeriesLookup = (stem: string) => string[];

// The members of `series(stem)` among `keys`: the rows `stem.<year>` in year order. A sibling
// whose last segment is digits but not a year is refused rather than dropped, because dropping
// it silently computes the horizon without it; `stem.1` beside `stem.2025` is a mistake to name,
// not a row to skip. One lookup for the stored ledger, the gates and the page, so the same
// formula reads the same rows everywhere.
export const seriesLookupFrom = (keys: Iterable<string>): TSeriesLookup => {
  const byStem = new Map<string, { members: string[]; stray: string[] }>();
  for (const key of keys) {
    const lastDot = key.lastIndexOf(".");
    if (lastDot < 0) continue;
    const stem = key.slice(0, lastDot);
    const entry = byStem.get(stem) ?? { members: [], stray: [] };
    if (YEAR_SUFFIX_RE.test(key)) entry.members.push(key);
    else if (DIGIT_SUFFIX_RE.test(key)) entry.stray.push(key);
    else continue;
    byStem.set(stem, entry);
  }
  return (stem) => {
    const entry = byStem.get(stem);
    if (!entry) return [];
    if (entry.stray.length > 0) {
      throw new Error(
        `series(${stem}) has sibling(s) ending in digits that are not a four-digit period: ${[...entry.stray].sort().join(", ")}`
      );
    }
    return [...entry.members].sort((a, b) => a.localeCompare(b));
  };
};

// Every declared key a node reads, the members of each series included, so that they are
// evaluated first and recorded as what the figure depends on.
export const collectKeys = (node: TNode, into: string[], seriesMembers: TSeriesLookup) => {
  const add = (key: string) => {
    if (!into.includes(key)) into.push(key);
  };
  if (node.t === "key") add(node.k);
  else if (node.t === "neg") collectKeys(node.a, into, seriesMembers);
  else if (node.t === "bin") {
    collectKeys(node.a, into, seriesMembers);
    collectKeys(node.b, into, seriesMembers);
  } else if (node.t === "series") seriesMembers(node.stem).forEach(add);
  else if (node.t === "call") node.args.forEach((arg) => collectKeys(arg, into, seriesMembers));
};

// The figures a relation names, as the model counts them: a key once, a series once by its stem.
// `sum(series(x)) == sum(series(x))` names one thing and checks nothing, however many rows the
// series expands to.
export const relationFigures = (relation: TRelationNode): string[] => {
  const named: string[] = [];
  const walk = (node: TNode) => {
    if (node.t === "key" && !named.includes(node.k)) named.push(node.k);
    else if (node.t === "series" && !named.includes(node.stem)) named.push(node.stem);
    else if (node.t === "neg") walk(node.a);
    else if (node.t === "bin") {
      walk(node.a);
      walk(node.b);
    } else if (node.t === "call") node.args.forEach(walk);
  };
  relation.parts.forEach(walk);
  return named;
};

export type TValue = number | number[];

export const asScalar = (value: TValue, what: string): number => {
  if (typeof value !== "number") throw new Error(`${what} needs a number, not a series`);
  return value;
};

export const asSeries = (value: TValue, what: string): number[] => {
  if (!Array.isArray(value)) throw new Error(`${what} needs a series`);
  return value;
};

export const npv = (rate: number, flows: number[]) => {
  if (rate <= -1) throw new NoReturnError(`npv() needs a rate above -1, got ${rate}`);
  return flows.reduce((total, flow, i) => total + flow / (1 + rate) ** i, 0);
};

// Bisection over the bracket every prototype uses, -99% to 5000%. Flows that never cross zero
// inside it have no rate that brings them to zero, so there is no IRR; the bound that used to be
// returned instead reached the ledger and the chapters as -95% or 500%.
export const irr = (flows: number[]) => {
  let lo = -0.99;
  let hi = 50;
  if (npv(lo, flows) * npv(hi, flows) >= 0) throw new NoReturnError("irr(): the flows never cross zero, so there is no rate");
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (npv(lo, flows) * npv(mid, flows) <= 0) hi = mid;
    else lo = mid;
  }
  return (lo + hi) / 2;
};

// Periods until the running total, having gone negative, comes back to zero or above; the
// fraction of the period in which it does. A series that never goes negative has nothing to
// recover and is refused rather than answered.
// A running total within floating-point noise of zero has recovered: -1.1 - 2.2 + 3.3 is -4.4e-16.
export const payback = (flows: number[]) => {
  const slack = (Math.max(...flows.map(Math.abs), 0) || 1) * 1e-9;
  let run = 0;
  let spent = false;
  for (let i = 0; i < flows.length; i++) {
    run += flows[i];
    if (run < -slack) spent = true;
    else if (spent) return i;
  }
  if (!spent) {
    throw new NoReturnError("payback(): the running total never goes negative, so there is no outlay to pay back");
  }
  throw new NoReturnError("payback(): the series never turns positive, so it has no payback period");
};

export const evaluate = (node: TNode, env: Record<string, number>, seriesMembers: TSeriesLookup): TValue => {
  switch (node.t) {
    case "num":
      return node.v;
    case "key":
      return env[node.k];
    case "neg":
      return -asScalar(evaluate(node.a, env, seriesMembers), "unary minus");
    case "bin": {
      const a = asScalar(evaluate(node.a, env, seriesMembers), `"${node.op}"`);
      const b = asScalar(evaluate(node.b, env, seriesMembers), `"${node.op}"`);
      if (node.op === "/" && b === 0) throw new Error("a division by zero");
      const result =
        node.op === "+" ? a + b : node.op === "-" ? a - b : node.op === "*" ? a * b : node.op === "/" ? a / b : a ** b;
      // A non-finite result is refused here, at the operator, the way the model that writes the
      // ledger refuses it: let through, Infinity is laundered by the next min() or npv() into a
      // finite number that passes every check downstream and reaches the page as a real one.
      if (!Number.isFinite(result)) throw new Error(`"${node.op}" did not produce a finite number`);
      return result;
    }
    case "series": {
      const members = seriesMembers(node.stem);
      if (members.length < 2) throw new Error(`series(${node.stem}) needs at least two yearly figures`);
      const values = members.map((key) => env[key]);
      // A hole in the series is not a zero. Left alone it reaches irr(), whose bisection walks to
      // its own bound and returns 5 — a legitimate-looking 500% from a year that is simply missing.
      const missing = members.filter((_, index) => !Number.isFinite(values[index]));
      if (missing.length > 0) throw new Error(`series(${node.stem}) is missing ${missing.join(", ")}`);
      return values;
    }
    case "call": {
      const values = node.args.map((arg) => evaluate(arg, env, seriesMembers));
      const result = evaluateCall(node.fn, values);
      if (typeof result === "number" && !Number.isFinite(result)) {
        throw new Error(`${node.fn}() did not produce a finite number`);
      }
      return result;
    }
  }
};

const evaluateCall = (fn: TFunctionName, values: TValue[]): TValue => {
  switch (fn) {
    case "npv":
      return npv(asScalar(values[0], "npv() rate"), asSeries(values[1], "npv()"));
    case "irr":
      return irr(asSeries(values[0], "irr()"));
    case "payback":
      return payback(asSeries(values[0], "payback()"));
    case "sum":
      return asSeries(values[0], "sum()").reduce((total, v) => total + v, 0);
    case "min":
    case "max": {
      const pick = fn === "min" ? Math.min : Math.max;
      const numbers =
        values.length === 1 ? asSeries(values[0], `${fn}()`) : values.map((value) => asScalar(value, `${fn}()`));
      if (numbers.length === 0) throw new Error(`${fn}() over nothing`);
      return pick(...numbers);
    }
    case "last": {
      const series = asSeries(values[0], "last()");
      return series[series.length - 1];
    }
    default:
      throw new Error(`unknown function ${fn}`);
  }
};

// Half a unit in the last declared place: a figure stated to one decimal is checked to one decimal,
// not to the double's full precision and not to a share of its size. A share of its size, which
// this once was, let a 5,000 gap in a million pass as equal, a thousandfold looser than the model
// that wrote the ledger; the same rule as `ledger_helper.py`'s `tolerance()`.
export const halfUnit = (dp: number) => 0.5 * 10 ** -dp;

// The slack a relation is checked with: the coarsest of the figures it compares, since the relation
// really does read those rows and inherits the roughest of their roundings.
export const relationTolerance = (dps: number[]) => (dps.length === 0 ? 0 : Math.max(...dps.map(halfUnit)));

// `==` and `!=` share one tolerance, so no pair of figures can both equal and differ; the ordering
// comparisons have none, as the model has none.
export const compare = (a: number, b: number, op: TComparison, eps: number) => {
  switch (op) {
    case "<":
      return a < b;
    case "<=":
      return a <= b;
    case ">":
      return a > b;
    case ">=":
      return a >= b;
    case "==":
      return Math.abs(a - b) <= eps;
    case "!=":
      return Math.abs(a - b) > eps;
  }
};

/** Rounds the way the model that wrote the ledger rounds, so a figure recomputed here lands on the
 *  number the analysis published. Python rounds the double's own exact decimal expansion, taking a
 *  half to the nearest even; `Math.round(value * 10 ** dp)` cannot reproduce that, because the
 *  multiplication has its own error (72.095 * 100 is 7209.499999999999) and because JavaScript
 *  takes a half upwards. The expansion is read as text instead, which is exact for every double a
 *  ledger carries. */
export const roundToDp = (value: number, dp: number): number => {
  if (!Number.isFinite(value)) return value;
  // Past this the double carries no fraction to round at `dp`, `toFixed` answers in exponential
  // form, and rebuilding the integer from its digits would lose precision that rounding was never
  // going to change: 999000000000000000000 came back as 998999999999999900000, and 1e21 as 0.01.
  if (Math.abs(value) >= Number.MAX_SAFE_INTEGER / 10 ** dp) return value;
  const negative = value < 0;
  // `toFixed` works from the exact value, and 25 digits past the decimals asked for is far more
  // than any figure needs to tell a true half from arithmetic that merely lands near one.
  const text = Math.abs(value).toFixed(Math.min(100, dp + 25));
  const point = text.indexOf(".");
  const digits = text.slice(0, point) + text.slice(point + 1);
  const cut = point + dp;
  const kept = digits.slice(0, cut);
  const dropped = digits.slice(cut);
  const isHalf = dropped[0] === "5" && /^0*$/.test(dropped.slice(1));
  const last = Number(kept[kept.length - 1] ?? "0");
  const roundUp = isHalf ? last % 2 === 1 : Number(dropped[0] ?? "0") >= 5;
  // Plain arithmetic on the kept digits: a ledger figure never reaches the range where a double
  // cannot hold its own integer part, and BigInt is not available to every target here.
  const scaled = Number(kept || "0") + (roundUp ? 1 : 0);
  const rounded = scaled / 10 ** dp;
  return negative && rounded !== 0 ? -rounded : rounded;
};

// The decimals a figure is rounded to when the model named none, read off its unit exactly as
// `ledger_helper.py` reads it: a count of things has none, a share or a rate one, a multiple two, an
// amount two. "per share" and "/share" are prices, not shares. The two tables have to agree, or a
// row without `dp` rounds one way in the sandbox and another in the store; the parity test holds
// them to it.
export const DEFAULT_DP = 2;
const UNIT_DP: [RegExp, number][] = [
  [/^(cnt|count|units?|sites?|stores?|customers?|people|staff|fte|hours?|days?|months?|years?|seats?|beds?|patients?|vehicles?|chargers?)$/i, 0],
  [/(?<![a-z])(?<!per )(?<!\/)(%|pct|percent|share|rate|points?)(?![a-z])/i, 1],
  [/^(x|×|multiple|ratio)$/i, 2]
];
export const defaultDp = (unit: string | null | undefined): number => {
  for (const [pattern, dp] of UNIT_DP) if (unit && pattern.test(unit)) return dp;
  return DEFAULT_DP;
};
